import {
  advantage,
  effectiveHitPoints,
  LUMINOSITY_STEPS,
  quietTurnsLeft,
  type SpellId,
  type Threat,
  type BoardDef,
} from '@archon3d/core';
import type { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import type { GuideHint } from '../../game/guide';
import type { GameSession, SessionSnapshot, SpellButton } from '../../game/session';
import {
  DEFAULT_DEPTH,
  DEFAULT_DUEL,
  DUEL_LEVELS,
  SETUP_PRESETS,
  presetOf,
  presetSetup,
  type SeatSetup,
  type SetupPreset,
} from '../../ui/gameSetup';
import { AI_DEPTHS } from '../../ui/settings';
import type { ShellState, ShellStore } from '../../ui/shell';
import { BOARD_CENTER, FAR_TABLET_LAYOUT, TABLE_HEIGHT } from '../board/constants';
import { oddsLabel } from '../board/odds';
import { PALETTES, factionColor, type Palette } from '../board/palette';
import { threatsToMover, worstThreat } from '../board/threatHints';
import { Tablet, type TabletItem } from './tablet';
import { cycleStonesWidth, type BalanceSegment, type GemKind, type Rect } from './tabletCanvas';
import { tabletRulePages, type RulePage } from '../../ui/ruleBook';
import { combatItems } from './combatItems';
import { drawItems } from './drawItems';
import { sideName } from '../../game/sideName';
import { immersiveMenuItems, type ImmersivePage } from './immersiveMenu';
import { wrapWords } from './wrapWords';
import type { ReadingFace } from './legibility';
import { endReason, matchSummary } from '../../game/matchSummary';

export interface GameTabletsDeps {
  scene: Scene;
  parent: TransformNode | null;
  session: GameSession;
  shell: ShellStore;
  resetView?(): void;
  /** A gem was tapped on any tablet (for sound); `enabled` false when it was a dead gem. */
  onGemTap?: (gem: { id: string; enabled: boolean }) => void;
}

/** A gem a gamepad cursor can rest on, placed on one plane with the three tablets side by side. */
export interface TabletGem {
  /** `tablet:gem`, stable while the gem is drawn. */
  key: string;
  /** Centre on the shared plane, in canvas pixels. */
  x: number;
  y: number;
  enabled: boolean;
}

export interface GameTablets {
  /** A pick anywhere in the scene; true when a tablet took it. */
  tap(pick: PickingInfo): boolean;
  /** Every gem on the three tablets, laid out on one plane for a gamepad to step between. */
  focusable(): TabletGem[];
  /** Frames a gem for the gamepad, or nothing. */
  setFocus(key: string | null): void;
  /** Presses a gem by key, as a tap on it would; false when it is not there any more. */
  press(key: string): boolean;
  /** Call every frame. */
  update(dtSeconds: number, inXr: boolean): void;
  /** Whether the action tablet names the enemy that threatens the selected piece (a setting). */
  setThreatHints(on: boolean): void;
  /** Everything written on each tablet, for tests. */
  text(): Record<'turn' | 'action' | 'game', string>;
  /** The faction colours of the score bar and the cycle stones follow the board's palette. */
  setPalette(palette: Palette): void;
  /** Multiplies every letter's size; 1 is the drawn design, `LARGE_TEXT` the accessible one. */
  setTextScale(scale: number): void;
  dispose(): void;
}

/** Tablet size in metres and canvas pixels (about four pixels per millimetre). */
const WIDTH = 0.26;
const HEIGHT = 0.17;
const TEX_W = 1040;
const TEX_H = 680;
const TILT = (32 * Math.PI) / 180;
/** The near tablets lean on the table's near edge: their middles this high over it, this far out. */
const NEAR_LIFT = (HEIGHT / 2) * Math.sin(TILT) + 0.012;
const NEAR_Z = BOARD_CENTER.z + 0.6;
/**
 * Each face as the eye meets it, for the smallest letter it may carry (`legibleSize`): 34 px on a
 * near tablet, 46 px on the sign, which hangs three times as far off at half the pixels a metre.
 */
export const TABLET_FACES: Record<'near' | 'sign', ReadingFace> = {
  near: { pxPerMetre: TEX_W / WIDTH, centre: new Vector3(0.15, TABLE_HEIGHT + NEAR_LIFT, NEAR_Z) },
  sign: {
    pxPerMetre: TEX_W / FAR_TABLET_LAYOUT.width,
    centre: new Vector3(0, FAR_TABLET_LAYOUT.y, FAR_TABLET_LAYOUT.z),
  },
};
/** Margin inside the bevel and screws. */
const M = 78;
/** The sign's texture width and its margin, for tests of what fits on it. */
export const SIGN_BOUNDS = { width: TEX_W, margin: M };
const CONTENT_W = TEX_W - 2 * M;
const GEM_H = 78;
/** Where the spell gems start when the piece's lines above them fit in the usual two. */
const GEMS_TOP = 212;
/**
 * The spell grid's three rows end high enough above this for the tip line and Shift Time's gem
 * (104 px between them) to keep off the tablet's bottom edge.
 */
const SPELLS_BOTTOM = TEX_H - 20 - 104;
/** The magic bulb in the action tablet's top right corner, and the room its lines leave it. */
const BULB_R = 64;
const BULB_ROOM = 2 * BULB_R + 24;
/**
 * Characters a piece's line may hold before it wraps: the content width less the magic bulb's
 * corner, at the line's 34 px lettering (about 17 px a glyph on average).
 */
const PIECE_LINE_CHARS = 42;
/** Characters a line of a lesson on the sign holds at its 46 px lettering. */
const LESSON_LINE_CHARS = 38;

/** The row of game buttons along the bottom edge, and the band the AI strengths fill above it. */
const BOTTOM_ROW = 600;
/** Up to this many AI seats get a roomy block each, this tall: a name line and two lamp rows. */
const ROOMY_AI_SEATS = 2;
const ROOMY_AI_BLOCK = 164;
/** The large-text scale: a quarter bigger, as far as the layouts stretch without a redesign. */
export const LARGE_TEXT = 1.25;

const SPELL_LABELS: Record<SpellId, string> = {
  teleport: 'Teleport',
  heal: 'Heal',
  'shift-time': 'Shift Time',
  exchange: 'Exchange',
  revive: 'Revive',
  imprison: 'Imprison',
  curse: 'Curse',
  freeze: 'Freeze',
  release: 'Release',
  // The gem is too narrow for the full name; the hint says what is summoned.
  'summon-elemental': 'Summon',
};

/**
 * The three tablets on the player's side of the table, grouped by what they are for:
 * the turn (what is happening), actions (what the player can do now) and the game (setup,
 * VR, server). Gem colours keep one meaning everywhere: amethyst casts spells, ruby starts fights,
 * emerald picks an option, diamond runs the game and onyx goes back.
 */
export function createGameTablets(deps: GameTabletsDeps): GameTablets {
  const { scene, parent, session, shell } = deps;
  const make = (name: string, x: number) =>
    new Tablet(scene, {
      name,
      width: WIDTH,
      height: HEIGHT,
      texWidth: TEX_W,
      texHeight: TEX_H,
      position: new Vector3(x, TABLE_HEIGHT + NEAR_LIFT, NEAR_Z),
      tilt: TILT,
      parent,
      onGemTap: deps.onGemTap,
    });
  // What is happening floats beyond the far edge of the table, facing the player; what the
  // player can do lies on the near edge, to the left and right of the middle.
  const turn = new Tablet(scene, {
    name: 'turn',
    width: FAR_TABLET_LAYOUT.width,
    height: FAR_TABLET_LAYOUT.height,
    texWidth: TEX_W,
    texHeight: TEX_H,
    position: new Vector3(0, FAR_TABLET_LAYOUT.y, FAR_TABLET_LAYOUT.z),
    tilt: FAR_TABLET_LAYOUT.tilt,
    parent,
    floating: true,
    onGemTap: deps.onGemTap,
  });
  const action = make('action', 0.15);
  const game = make('game', -0.15);
  const all = [turn, action, game];
  // Where each tablet's canvas sits on the plane a gamepad cursor walks: the two on the near edge
  // side by side as the player sees them (action on the left), the sign above and between them.
  const GAP = 160;
  const plane: { name: string; tablet: Tablet; dx: number; dy: number }[] = [
    { name: 'turn', tablet: turn, dx: (TEX_W + GAP) / 2, dy: -(TEX_H + GAP) },
    { name: 'action', tablet: action, dx: 0, dy: 0 },
    { name: 'game', tablet: game, dx: TEX_W + GAP, dy: 0 },
  ];

  const wood = new Image();
  wood.onload = () => all.forEach((t) => t.setWood(wood));
  wood.src = '/textures/wood/walnut_color.jpg';

  let reviveMenu = false;
  let threatHints = true;
  let clock = 0;
  let palette: Palette = PALETTES.classic;
  let textScale = 1;
  let rulePage: number | null = null;
  let combatPage = 0;
  let immersivePage: ImmersivePage = 'closed';
  let releaseMenu: (() => void) | null = null;
  const navigate = (page: ImmersivePage) => {
    immersivePage = page;
    if (page !== 'closed' && shell.getSnapshot().playing && !releaseMenu)
      releaseMenu = session.hold();
    if (page === 'closed') {
      const release = releaseMenu;
      releaseMenu = null;
      release?.();
    }
    rebuild();
  };

  const rebuild = () => {
    const snapshot = session.getSnapshot();
    const state = shell.getSnapshot();
    if (!state.inXr && immersivePage !== 'closed') {
      immersivePage = 'closed';
      rulePage = null;
      const release = releaseMenu;
      releaseMenu = null;
      release?.();
    }
    if (snapshot.targeting?.spell !== 'revive' && snapshot.state.phase !== 'play')
      reviveMenu = false;
    // The sign is already half as big again and hangs two metres away: it grows less.
    const signScale = 1 + (textScale - 1) * 0.6;
    turn.setItems(
      turnItems(
        snapshot,
        palette,
        signScale,
        state.guide,
        () => shell.onNewGame?.(state.table),
        () => shell.onGuideDismiss?.(),
      ),
    );
    const threat = threatHints
      ? worstThreat(threatsToMover(snapshot), snapshot.selectedPieceId)
      : undefined;
    action.setItems(
      drawItems(snapshot, session, textScale) ??
        combatItems(
          snapshot,
          session,
          combatPage,
          (page) => {
            combatPage = page;
            rebuild();
          },
          textScale,
        ) ??
        actionItems(snapshot, session, reviveMenu, textScale, threat, (open) => {
          reviveMenu = open;
          rebuild();
        }),
    );
    game.setItems(
      state.inXr && rulePage === null && (immersivePage !== 'closed' || !state.playing)
        ? immersiveMenuItems(
            shell,
            immersivePage,
            {
              navigate,
              rules: () => {
                rulePage = 0;
                rebuild();
              },
              reset: () => deps.resetView?.(),
            },
            textScale,
          )
        : gameItems(
            snapshot,
            state,
            deps,
            textScale,
            rulePage,
            (page) => {
              rulePage = page;
              rebuild();
            },
            () => (shell.onMenu ? shell.onMenu() : navigate('menu')),
          ),
    );
  };
  const unsubscribeSession = session.subscribe(rebuild);
  const unsubscribeShell = shell.subscribe(rebuild);
  const unsubscribeSettings = shell.settings?.subscribe(rebuild);
  const unsubscribeRadio = shell.radio?.subscribe(rebuild);
  rebuild();

  return {
    tap(pick) {
      if (
        shell.getSnapshot().inXr &&
        (immersivePage !== 'closed' || !shell.getSnapshot().playing)
      ) {
        game.tap(pick, clock);
        return true;
      }
      return all.some((t) => t.tap(pick, clock));
    },
    focusable: () =>
      plane.flatMap(({ name, tablet, dx, dy }) =>
        tablet.gems().map((gem) => ({
          key: `${name}:${gem.id}`,
          x: dx + gem.rect.x + gem.rect.w / 2,
          y: dy + gem.rect.y + gem.rect.h / 2,
          enabled: gem.enabled,
        })),
      ),
    setFocus(key) {
      for (const { name, tablet } of plane)
        tablet.setFocus(key?.startsWith(`${name}:`) ? key.slice(name.length + 1) : null);
    },
    press(key) {
      const entry = plane.find(({ name }) => key.startsWith(`${name}:`));
      return entry ? entry.tablet.press(key.slice(entry.name.length + 1), clock) : false;
    },
    update(dt, inXr) {
      clock += dt;
      // In the headset the gems keep still: a sparkle meant a whole face repainted and re-uploaded
      // six times a second, for each tablet with a live gem.
      for (const t of all) t.update(clock, inXr ? 0 : 12);
    },
    setThreatHints(on) {
      if (on === threatHints) return;
      threatHints = on;
      rebuild();
    },
    text: () => ({ turn: turn.text(), action: action.text(), game: game.text() }),
    setPalette(next) {
      if (next === palette) return;
      palette = next;
      rebuild();
    },
    setTextScale(scale) {
      if (scale === textScale) return;
      textScale = scale;
      rebuild();
    },
    dispose() {
      unsubscribeSession();
      unsubscribeShell();
      unsubscribeSettings?.();
      unsubscribeRadio?.();
      releaseMenu?.();
      for (const t of all) t.dispose();
    },
  };
}

function factionName(snapshot: SessionSnapshot, id: string | null): string {
  return id === null ? '' : sideName(snapshot, id);
}

export function turnItems(
  snapshot: SessionSnapshot,
  palette: Palette,
  k: number,
  guide: GuideHint | null,
  onPlayAgain: () => void,
  onGotIt: () => void,
): TabletItem[] {
  const { state, seats } = snapshot;
  const items: TabletItem[] = [];
  const centre = TEX_W / 2;
  let title: string;
  if (state.phase === 'ended')
    title = state.winner === 'draw' ? 'A draw' : `${factionName(snapshot, state.winner)} wins`;
  else if (state.phase === 'battle') title = 'Battle!';
  else
    title = `${factionName(snapshot, state.activeFaction)} to move${seats[state.activeFaction] === 'ai' ? ' (AI)' : ''}`;
  // The sign hangs two metres away: everything on it is half as big again as on the tablets.
  items.push({
    kind: 'text',
    text: title,
    x: centre,
    y: 170,
    size: 88 * k,
    align: 'center',
    maxWidth: CONTENT_W,
  });
  // Nothing on the sign is smaller than a letter the headset can read from the seat, 46 px at its
  // distance (`TABLET_FACES`): the score's names, a lesson's lines and the round were not.
  // The round, the turn and the light cycle share a row a little wider than the column below, so
  // that the cycle's six stones fit on the sign at a size that reads: the last ran off its edge
  // (the owner, 2026-09-26). The stones go from the darkest light to the brightest; the ringed one
  // is where the board's light stands now.
  const row = { left: centre - 390, right: centre + 390 };
  items.push({
    kind: 'plaque',
    rect: { x: row.left, y: 228, w: 240, h: 76 },
    text: `Round ${state.round + 1}`,
    size: 46 * k,
  });
  items.push({
    kind: 'plaque',
    rect: { x: row.left + 254, y: 228, w: 220, h: 76 },
    text: `Turn ${state.turn + 1}`,
    size: 46 * k,
  });
  const stones = row.left + 490;
  items.push({
    kind: 'stones',
    x: stones,
    y: 266,
    r: (row.right - stones) / cycleStonesWidth(LUMINOSITY_STEPS, 1),
    colors: palette.luminosityShades.slice(0, LUMINOSITY_STEPS),
    phase: state.luminosity.phase,
  });
  // The score: each side's share of the advantage, as the AI's evaluation sees the position.
  items.push({
    kind: 'balance',
    rect: { x: centre - 340, y: 322, w: 680, h: 64 },
    segments: scoreSegments(snapshot, palette),
    fontSize: 46 * k,
  });
  // A lesson of the guided first game takes the lower half of the sign, in place of the last
  // event. Its lines are wrapped rather than squeezed into one, and stay at the floor's size with
  // large text too: three lines of it are all the sign holds.
  // A draw offered or answered is news the person is waiting on: it takes the lesson's place until
  // the next thing happens. So does the quiet-turn draw counted down (core-rules:quality#13): a
  // newcomer still reading the lessons is the one it would otherwise take by surprise.
  const quiet = quietTurnsLeft(state);
  const countdown = state.phase === 'play' && quiet <= QUIET_WARNING_TURNS;
  const lesson =
    guide && state.phase !== 'ended' && snapshot.lastEvent?.type !== 'draw' && !countdown
      ? guide
      : null;
  const summary = matchSummary(snapshot.ctx, state, snapshot.names);
  if (lesson) {
    items.push({
      kind: 'text',
      text: lesson.title,
      x: M,
      y: 450,
      size: 46,
      weight: 700,
      maxWidth: CONTENT_W - 280,
    });
    items.push({
      kind: 'gem',
      id: 'guide-got-it',
      rect: { x: TEX_W - M - 250, y: 404, w: 250, h: 64 },
      gem: 'emerald',
      label: 'Got it',
      enabled: true,
      fontSize: 46,
      onTap: onGotIt,
    });
    wrapWords(lesson.text, LESSON_LINE_CHARS, 3).forEach((line, i) =>
      items.push({
        kind: 'text',
        text: line,
        x: centre,
        y: 506 + i * 48,
        size: 46,
        align: 'center',
        weight: 600,
        alpha: 0.92,
        maxWidth: CONTENT_W,
      }),
    );
  } else if (summary)
    // The game is over: how it was won, where the headset has no result plate to say so.
    items.push({
      kind: 'text',
      text: endReason(summary),
      x: centre,
      y: 456,
      size: 46 * k,
      align: 'center',
      weight: 600,
      maxWidth: CONTENT_W,
    });
  else if (snapshot.lastEvent)
    items.push({
      kind: 'text',
      text: snapshot.lastEvent.text,
      x: centre,
      y: 456,
      size: 50 * k,
      align: 'center',
      weight: 600,
      maxWidth: CONTENT_W,
    });
  // The quiet-turn draw, counted down over its last turns rather than called without warning.
  if (countdown)
    items.push({
      kind: 'text',
      text: `Draw in ${quiet} quiet turn${quiet === 1 ? '' : 's'} · a fight, Heal or Revive resets it`,
      x: centre,
      y: 540,
      size: 46 * k,
      align: 'center',
      weight: 600,
      alpha: 0.92,
      maxWidth: CONTENT_W,
    });
  // A room cannot be started again from the sign; its banner leads back to the menu instead.
  if (state.phase === 'ended' && !snapshot.online)
    items.push({
      kind: 'gem',
      id: 'again',
      rect: { x: centre - 220, y: 492, w: 440, h: 100 },
      gem: 'diamond',
      label: 'Play again',
      enabled: true,
      fontSize: 46 * k,
      onTap: onPlayAgain,
    });
  return items;
}

/** From how many turns out the sign counts the quiet-turn draw down. */
export const QUIET_WARNING_TURNS = 10;

/** Every faction still in the game, with its share of the evaluation's advantage. */
function scoreSegments(snapshot: SessionSnapshot, palette: Palette): BalanceSegment[] {
  const { ctx, state } = snapshot;
  const alive = ctx.board.def.factions.filter(
    (f) => !state.factions.find((s) => s.id === f.id)?.eliminated,
  );
  const scores = alive.map((f) => advantage(ctx, state, f.id));
  const total = scores.reduce((sum, v) => sum + v, 0) || 1;
  return alive.map((f, i) => ({
    label: f.name,
    color: factionColor(palette, f),
    share: scores[i]! / total,
  }));
}

export function actionItems(
  snapshot: SessionSnapshot,
  session: GameSession,
  reviveMenu: boolean,
  k: number,
  threat: Threat | undefined,
  setReviveMenu: (open: boolean) => void,
): TabletItem[] {
  const { ctx, state, seats, selectedPieceId, targeting } = snapshot;
  const items: TabletItem[] = [];
  const human = session.isHumanTurn() && !snapshot.busy;

  if (!human) {
    const text =
      state.phase === 'battle'
        ? 'The fight decides the square'
        : state.phase === 'ended'
          ? 'The game is over'
          : seats[state.activeFaction] === 'ai'
            ? `${factionName(snapshot, state.activeFaction)} is thinking…`
            : seats[state.activeFaction] === 'remote'
              ? `Waiting for ${factionName(snapshot, state.activeFaction)}`
              : 'A piece is on its way';
    items.push({
      kind: 'text',
      text,
      x: M,
      y: 118,
      size: 44 * k,
      weight: 600,
      maxWidth: CONTENT_W,
    });
    return items;
  }

  if (targeting) {
    items.push({
      kind: 'text',
      text: targetingHint(targeting.spell, targeting.pieceId !== undefined),
      x: M,
      y: 118,
      size: 40 * k,
      weight: 600,
      maxWidth: CONTENT_W,
    });
    items.push(cancelGem(M, 170, k, () => session.cancelSpell()));
    return items;
  }

  const faction = state.factions.find((f) => f.id === state.activeFaction);
  if (reviveMenu) {
    items.push({ kind: 'text', text: 'Revive whom?', x: M, y: 118, size: 44 * k });
    const characters = [...new Set(faction?.graveyard ?? [])];
    gemGrid(
      items,
      160,
      characters.map((c) => ({
        id: `revive-${c}`,
        gem: 'emerald' as const,
        label: ctx.roster[c]?.name ?? c,
        enabled: true,
        onTap: () => {
          setReviveMenu(false);
          session.beginSpell('revive', c);
        },
      })),
      3,
      { scale: k },
    );
    items.push(cancelGem(M, 460, k, () => setReviveMenu(false)));
    return items;
  }

  const selected = state.pieces.find((p) => p.id === selectedPieceId);
  const character = selected ? ctx.roster[selected.character] : undefined;
  // Where the spell gems start: below the piece's lines, however many the text needs.
  let gemsTop = GEMS_TOP;
  if (selected && character) {
    items.push({ kind: 'text', text: character.name, x: M, y: 110, size: 46 * k });
    // Whole numbers, as a player reads them; the fitted decimals underneath are the engine's.
    const hp = Math.round(effectiveHitPoints(ctx, state, selected));
    const wounded = Math.round(selected.damage);
    const specs = `${character.movement.type} ${character.movement.range} · ${hp} hp${wounded > 0 ? ` (${wounded} wounded)` : ''} · ${character.attack.type} ${Math.round(character.attack.damage)} damage`;
    // The lines stop short of the magic bulb in the corner and wrap rather than squeeze or run
    // under it; the gems move down to make room for a second line. Large text wraps sooner and
    // spaces its lines out with its letters.
    const chars = Math.floor(PIECE_LINE_CHARS / k);
    const pitch = Math.round(38 * k);
    let y = 156;
    for (const line of wrapWords(specs, chars, 3)) {
      items.push({
        kind: 'text',
        text: line,
        x: M,
        y,
        size: 34 * k,
        weight: 600,
        alpha: 0.85,
        maxWidth: CONTENT_W - BULB_ROOM,
      });
      y += pitch;
    }
    if (threat) {
      // One line, as large as the rest: it ignored the text size before (x-quest-vr#6).
      const by = ctx.roster[threat.attacker.character]?.name ?? threat.attacker.character;
      items.push({
        kind: 'text',
        text: `Threatened by ${by} (${oddsLabel(threat.probability)})`,
        x: M,
        y: y + 4,
        size: 34 * k,
        weight: 600,
        alpha: 0.9,
        maxWidth: CONTENT_W - BULB_ROOM,
      });
      y += pitch;
    }
    gemsTop = Math.max(GEMS_TOP, y + 4);
  } else {
    items.push({
      kind: 'text',
      text: 'Available Actions',
      x: M,
      y: 118,
      size: 38 * k,
      weight: 600,
      alpha: 0.9,
      maxWidth: CONTENT_W,
    });
  }

  // The magic the selected piece is carrying, in a bulb in the top-right corner. It keeps its
  // place and shows a dash for a piece that is no magician, rather than appearing and vanishing
  // and shifting everything under it about.
  const magic = session.selectedMagic();
  items.push({
    kind: 'bulb',
    x: TEX_W - M - BULB_R - 2,
    y: 74 + BULB_R,
    r: BULB_R,
    text: magic ? `${magic.points}` : '—',
    sub: magic ? `/ ${magic.most}` : '',
    lit: magic !== null && magic.points > 0,
    // Its figures fill the glass at any text size; larger, they would run over its rim.
    scale: 1,
  });

  // Every spell keeps its place whatever is selected, and says by its colour where it stands:
  // green to cast, red for one this piece knows and cannot cast now, grey for one it never knew.
  // A panel whose buttons come and go cannot be learned, and a player who has to hunt for Heal
  // because Teleport went away has stopped playing the game and started reading the panel.
  const panel = session.spellPanel();
  const spells = panel.filter(({ spell }) => spell !== 'shift-time');
  // The spells take what the lines above leave: a piece with three lines of figures and a threat,
  // in large text, gets flatter gems rather than a Shift Time gem off the tablet's edge.
  const gemH = Math.min(GEM_H, Math.floor((SPELLS_BOTTOM - gemsTop) / 3) - 18);
  if (spells.length > 0) {
    gemGrid(
      items,
      gemsTop,
      spells.map(({ spell, cost, known, castable, blocked }) => ({
        id: `spell-${spell}`,
        // Red covers every "knows it, cannot cast it": out of points, standing on a circle, held.
        // Nothing to aim at stays green, because the magic is there and the board is not.
        gem: !known
          ? ('amethyst' as const)
          : castable || blocked === 'no-target'
            ? ('emerald' as const)
            : ('ruby' as const),
        label: SPELL_LABELS[spell],
        cost,
        enabled: known,
        onTap: () => {
          if (!castable) return;
          if (spell === 'revive') setReviveMenu(true);
          else session.beginSpell(spell);
        },
      })),
      3,
      { height: gemH, scale: k, fontSize: 34 },
    );
    // One short line under the spells saying what is in the way, when one thing is.
    const tip = spellTip(spells);
    if (tip)
      items.push({
        kind: 'text',
        text: tip,
        x: M,
        y: gemsTop + 3 * (gemH + 18) + 14,
        size: 34 * k,
        italic: true,
        alpha: 0.85,
        maxWidth: CONTENT_W,
      });
  }

  // Shift Time is not a spell. It is the player letting the round pass, it costs nothing and
  // needs nobody to cast it, so it stands on its own below the eight and is always lit.
  gemGrid(
    items,
    gemsTop + 3 * (gemH + 18) + 38,
    [
      {
        id: 'spell-shift-time',
        gem: 'diamond' as const,
        label: SPELL_LABELS['shift-time'],
        enabled: true,
        onTap: () => session.beginSpell('shift-time'),
      },
    ],
    1,
    { height: 66, scale: k, fontSize: 34 },
  );
  return items;
}

/**
 * The near-left tablet. It is a *setup* panel while the table is idle and a *game* panel once a
 * game is running: the presets and Restart both throw the running game away or
 * distract from it, and in the headset this tablet is the only interface there is, so one stray
 * ray must not be able to discard a game — or, online, the room the opponent is sitting in.
 *
 * A running game leaves only controls that are about the view rather than about abandoning it.
 */
export function gameItems(
  snapshot: SessionSnapshot,
  shell: ShellState,
  deps: GameTabletsDeps,
  k: number,
  rulePage: number | null,
  setRulePage: (page: number | null) => void,
  openImmersiveMenu: () => void,
): TabletItem[] {
  if (rulePage !== null)
    return ruleItems(snapshot.ctx.roster, snapshot.ctx.definition?.board, k, rulePage, setRulePage);
  const items: TabletItem[] = [];
  let top = 150;
  if (shell.playing) {
    items.push({ kind: 'text', text: 'Game', x: M, y: 108, size: 48 * k });
  } else {
    items.push({ kind: 'text', text: 'New game', x: M, y: 108, size: 48 * k });
    const chosen = presetOf(shell.setup);
    gemGrid(
      items,
      136,
      SETUP_PRESETS.map((s) => ({
        id: `setup-${s.id}`,
        gem: 'emerald' as const,
        label: s.label,
        enabled: true,
        active: s.id === chosen,
        onTap: () => newGame(deps, s.id),
      })),
      4,
      { height: 38, scale: k },
    );
    gemGrid(
      items,
      186,
      [
        {
          id: 'restart',
          gem: 'diamond',
          label: 'Restart',
          enabled: true,
          onTap: () => deps.shell.onNewGame?.(shell.setup),
        },
      ],
      2,
      { height: 38, scale: k },
    );
    top = 234;
  }
  // The AI's strengths fill the middle; the buttons that run the game sit together along the
  // bottom, where a hand reaches for them, rather than above what they have nothing to do with.
  aiStrengths(items, snapshot, shell, k, top, BOTTOM_ROW - 14);
  // A person playing here may offer a draw on their turn, once a turn (D-46); greyed otherwise.
  const offer: GemSpec[] =
    shell.playing && snapshot.state.phase !== 'ended' && !snapshot.online?.spectator
      ? [
          {
            id: 'offer-draw',
            gem: 'ruby',
            label: 'Offer draw',
            enabled: deps.session.canOfferDraw(),
            onTap: () => deps.session.offerDraw(),
          },
        ]
      : [];
  const bottom: GemSpec[] = shell.inXr
    ? [
        ...offer,
        {
          id: 'menu',
          gem: 'diamond',
          label: 'Menu',
          enabled: true,
          onTap: openImmersiveMenu,
        },
      ]
    : [
        ...offer,
        {
          id: 'rules',
          gem: 'emerald',
          label: 'Rules',
          enabled: true,
          onTap: () => setRulePage(0),
        },
        {
          id: 'menu',
          gem: 'diamond',
          label: 'Menu',
          enabled: deps.shell.onMenu !== null,
          onTap: () => deps.shell.onMenu?.(),
        },
      ];
  gemGrid(items, BOTTOM_ROW, bottom, bottom.length, { height: 60, scale: k, fontSize: 34 });
  // No footer. The server's state and the build number are for whoever is debugging it, not for
  // a player at the table, and small grey lettering along the bottom edge of a wooden tablet is
  // unreadable from where anyone actually sits.
  return items;
}

/** A page of the rule book as the tablet prints it: its rules wrapped into rows. */
export interface RuleSheet {
  title: string;
  /** Rows of lettering; `first` begins a rule, which gets a little room above it. */
  rows: { text: string; first: boolean }[];
}

/** How the rule book's lettering is laid out on a sheet, in canvas pixels. */
export interface RuleLayout {
  /** Characters a row holds before it wraps. */
  chars: number;
  /** From one row's baseline to the next, and the extra room above a rule's first row. */
  pitch: number;
  gap: number;
  /** From the first row's baseline to the lowest a baseline may sit. */
  height: number;
}

/**
 * The rule book cut to the tablet: each rule wrapped into rows, and a page whose rows run past the
 * sheet carried onto another of the same title (client-tablets-ui:bugs#5). The rules had been
 * squeezed into one 884 px line each, up to six times too narrow to read.
 */
export function ruleSheets(pages: readonly RulePage[], layout: RuleLayout): RuleSheet[] {
  const sheets: RuleSheet[] = [];
  for (const page of pages) {
    let sheet: RuleSheet = { title: page.title, rows: [] };
    let lowest = -layout.pitch;
    for (const rule of page.lines) {
      wrapWords(rule, layout.chars, Infinity).forEach((text, i) => {
        const first = i === 0;
        const next = lowest + layout.pitch + (first && sheet.rows.length > 0 ? layout.gap : 0);
        if (next > layout.height && sheet.rows.length > 0) {
          sheets.push(sheet);
          sheet = { title: `${page.title} (continued)`, rows: [] };
          lowest = 0;
        } else lowest = next;
        sheet.rows.push({ text, first });
      });
    }
    if (sheet.rows.length > 0) sheets.push(sheet);
  }
  return sheets;
}

/** The rule book's rows: 34 px, about 17 px a glyph, from under the title to above the buttons. */
const RULE_LINE_CHARS = 50;
const RULE_TOP = 168;
const RULE_BOTTOM = BOTTOM_ROW - 26;

/** The full rule book, paged, as the only rule book inside immersive Quest VR. */
function ruleItems(
  roster: SessionSnapshot['ctx']['roster'],
  board: BoardDef | undefined,
  k: number,
  requestedPage: number,
  setPage: (page: number | null) => void,
): TabletItem[] {
  const pitch = Math.round(40 * k);
  const gap = Math.round(14 * k);
  const sheets = ruleSheets(tabletRulePages(roster, board), {
    chars: Math.floor(RULE_LINE_CHARS / k),
    pitch,
    gap,
    height: RULE_BOTTOM - RULE_TOP,
  });
  const pageIndex = Math.max(0, Math.min(sheets.length - 1, requestedPage));
  const sheet = sheets[pageIndex]!;
  const items: TabletItem[] = [
    {
      kind: 'text',
      text: sheet.title,
      x: M,
      y: 96,
      size: 43 * k,
      weight: 700,
      maxWidth: CONTENT_W - 150,
    },
    {
      kind: 'text',
      text: `${pageIndex + 1} / ${sheets.length}`,
      x: TEX_W - M,
      y: 96,
      size: 34 * k,
      align: 'right',
      alpha: 0.75,
    },
  ];
  let y = RULE_TOP;
  sheet.rows.forEach((row, index) => {
    if (index > 0) y += pitch + (row.first ? gap : 0);
    items.push({
      kind: 'text',
      text: row.text,
      x: M,
      y,
      size: 34 * k,
      weight: 600,
      maxWidth: CONTENT_W,
    });
  });
  const buttons: GemSpec[] = [
    {
      id: 'rules-prev',
      gem: 'diamond',
      label: 'Previous',
      enabled: pageIndex > 0,
      onTap: () => setPage(pageIndex - 1),
    },
    {
      id: 'rules-close',
      gem: 'onyx',
      label: 'Close',
      enabled: true,
      onTap: () => setPage(null),
    },
    {
      id: 'rules-next',
      gem: 'diamond',
      label: 'Next',
      enabled: pageIndex < sheets.length - 1,
      onTap: () => setPage(pageIndex + 1),
    },
  ];
  gemGrid(items, BOTTOM_ROW, buttons, 3, { height: 60, scale: k, fontSize: 34 });
  return items;
}

/**
 * How strong each AI is, read-only: one block per AI seat, naming the side and then showing its
 * two dials on rows that line up column for column — Tactics is how far it looks ahead on the
 * board, Combat is how it aims and moves in the arena. The level in force is lit and the other
 * three are grey, so the scale itself is on show rather than a bare word.
 *
 * Read-only on purpose. Strength is per seat — one AI may be a novice and the next a master — and
 * a row of buttons cannot set a matrix. It is set once, before the game, in Settings.
 */
export function aiStrengths(
  items: TabletItem[],
  snapshot: SessionSnapshot,
  shell: ShellState,
  k: number,
  top: number,
  bottom: number,
): void {
  const factions = snapshot.ctx.board.def.factions;
  const seats = shell.table.seats
    .map((seat, i) => ({ seat, name: factions[i]?.name ?? `Side ${i + 1}` }))
    .filter((s) => s.seat.kind === 'ai');
  if (seats.length === 0) return;
  const depthLabel = (seat: SeatSetup) =>
    AI_DEPTHS.find((d) => d.depth === (seat.depth ?? DEFAULT_DEPTH))?.label ?? '';
  const duelLabel = (seat: SeatSetup) => seat.duel ?? DEFAULT_DUEL;
  const combatLabel = (seat: SeatSetup) =>
    DUEL_LEVELS.find((d) => d.id === duelLabel(seat))?.label ?? '';
  const lamps = (
    dial: 'tactics' | 'combat',
    name: string,
    lit: string,
    at: number,
    grid: GridOptions,
  ) =>
    gemGrid(
      items,
      at,
      DUEL_LEVELS.map((d) => ({
        id: `ai-${dial}-${name}-${d.id}`,
        gem: 'emerald' as const,
        label: d.label,
        // A dead gem draws grey; only the level in force is lit. Nothing here is tappable.
        enabled: d.label === lit,
        active: d.label === lit,
        flat: true,
      })),
      4,
      grid,
    );

  // One AI or two, as nearly every table has: the name above its two rows of lamps, across the
  // whole tablet, all of it lettered as large as the headset reads (x-quest-vr#6).
  if (seats.length <= ROOMY_AI_SEATS && bottom - top >= seats.length * ROOMY_AI_BLOCK) {
    let y = top;
    for (const { seat, name } of seats) {
      items.push({
        kind: 'text',
        text: `${name.replace(/\s+AI$/i, '')} AI`,
        x: M,
        y: y + 34,
        size: 34 * k,
        weight: 700,
        maxWidth: CONTENT_W,
      });
      const row = (dial: 'tactics' | 'combat', at: number, lit: string) => {
        items.push({
          kind: 'text',
          text: dial === 'tactics' ? 'Tactics' : 'Combat',
          x: M,
          y: at + 37,
          size: 34 * k,
          alpha: 0.85,
          maxWidth: 140,
        });
        // The level names stay at the floor's size: larger, they would not fit a lamp.
        lamps(dial, name, lit, at, {
          height: 50,
          x0: M + 150,
          width: CONTENT_W - 150,
          fontSize: 34,
        });
      };
      row('tactics', y + 50, depthLabel(seat));
      row('combat', y + 108, combatLabel(seat));
      y += ROOMY_AI_BLOCK;
    }
    return;
  }

  // Reserve six compact blocks; never replace the eight lamps with a text-only fallback.
  const rowHeight = Math.min(78 * 0.4, ((bottom - top) / 6 - 8) / 2);
  const labelWidth = 130;
  const nameWidth = 215;
  const gemsWidth = (CONTENT_W - 150 - 54) * 0.7 + 54;
  const gemsX = TEX_W - M - gemsWidth;
  const seatHeight = 2 * rowHeight + 8;
  let y = top;
  for (const { seat, name } of seats) {
    items.push({
      kind: 'text',
      text: `${name.replace(/\s+AI$/i, '').slice(0, 12)} AI`,
      x: M,
      y: y + rowHeight + 10,
      size: 27 * k,
      maxWidth: nameWidth - 12,
    });
    // Tactics above, Combat below, the same four columns in both so the eye can read down them.
    const row = (dial: 'tactics' | 'combat', at: number, lit: string) => {
      items.push({
        kind: 'text',
        text: dial === 'tactics' ? 'Tactics' : 'Combat',
        x: M + nameWidth,
        y: at + rowHeight / 2 + 8,
        size: 23 * k,
        alpha: 0.85,
        maxWidth: labelWidth - 12,
      });
      lamps(dial, name, lit, at, {
        height: rowHeight,
        x0: gemsX,
        width: gemsWidth,
        scale: k,
        fontSize: 26,
      });
    };
    row('tactics', y, depthLabel(seat));
    row('combat', y + rowHeight + 3, combatLabel(seat));
    y += seatHeight;
  }
}

/** A preset from the tablet becomes a full setup for the board in play, keeping the AI levels. */
function newGame(deps: GameTabletsDeps, preset: SetupPreset): void {
  const board = deps.session.getSnapshot().ctx.board.def;
  deps.shell.onNewGame?.(presetSetup(board, preset, deps.shell.getSnapshot().setup));
}

interface GemSpec {
  id: string;
  gem: GemKind;
  label: string;
  enabled: boolean;
  active?: boolean;
  flat?: boolean;
  onTap?: () => void;
}

interface GridOptions {
  fontSize?: number;
  /** Gem height in pixels, `GEM_H` unless given. */
  height?: number;
  /** Left edge and width of the grid; the content area unless given. */
  x0?: number;
  width?: number;
  /** Text scale for the labels. */
  scale?: number;
}

/** Lays gems out in rows of `cols`, filling the content width unless given a narrower span. */
function gemGrid(
  items: TabletItem[],
  y: number,
  gems: GemSpec[],
  cols: number,
  { height = GEM_H, x0 = M, width = CONTENT_W, scale = 1, fontSize }: GridOptions = {},
): void {
  const gap = 18;
  const w = (width - gap * (cols - 1)) / cols;
  gems.forEach((g, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const rect: Rect = { x: x0 + col * (w + gap), y: y + row * (height + gap), w, h: height };
    items.push({
      kind: 'gem',
      rect,
      fontSize: (fontSize ?? Math.min(34, height * 0.44)) * scale,
      ...g,
    });
  });
}

function cancelGem(x: number, y: number, scale: number, onTap: () => void): TabletItem {
  return {
    kind: 'gem',
    id: 'cancel',
    rect: { x, y, w: 300, h: GEM_H },
    gem: 'onyx',
    label: 'Cancel',
    enabled: true,
    fontSize: 34 * scale,
    onTap,
  };
}

/**
 * The one line under the spells. Short on purpose: a player glancing down mid-turn reads four
 * words, not a sentence, and a tip that explains everything explains nothing. Only shown when one
 * thing is in the way of everything — several reasons at once is the panel's own colours doing
 * the telling.
 */
function spellTip(spells: readonly SpellButton[]): string | null {
  const known = spells.filter((s) => s.known);
  if (known.length === 0) return 'No magic in this one.';
  if (known.some((s) => s.castable)) return null;
  const reasons = new Set(known.map((s) => s.blocked));
  if (reasons.size !== 1) return null;
  switch ([...reasons][0]) {
    case 'circle':
      return 'No magic on circles. Step off to cast.';
    case 'poor':
      return 'Not enough magic. Rest on a circle.';
    case 'held':
      return 'Held fast. It cannot cast.';
    case 'no-target':
      return 'Nothing to aim at.';
    default:
      return null;
  }
}

function targetingHint(spell: SpellId, pickedPiece: boolean): string {
  switch (spell) {
    case 'teleport':
      return pickedPiece ? 'Pick the destination square' : 'Pick the piece to teleport';
    case 'heal':
      return 'Pick the piece to heal';
    case 'imprison':
      return 'Pick the enemy to imprison';
    case 'curse':
      return 'Pick the enemy to curse: it fights slowed';
    case 'freeze':
      // Its own moves only: Teleport and Exchange may still carry it (core-rules:quality#8).
      return 'Pick the enemy to freeze: it cannot move itself';
    case 'release':
      return 'Pick one of yours to free';
    case 'exchange':
      return pickedPiece ? 'Pick the second piece' : 'Pick the first piece to exchange';
    case 'revive':
      return 'Pick a square next to your mage';
    case 'summon-elemental':
      return pickedPiece
        ? 'Pick the square the elemental appears on'
        : 'Pick the enemy the elemental attacks';
    case 'shift-time':
      return '';
  }
}
